import { useState, useEffect } from "react";
import { MessageCircle, X } from "lucide-react";

// Configurable via env. International format, digits only.
const WHATSAPP_NUMBER =
  (process.env.REACT_APP_WHATSAPP_NUMBER || "441619894842").replace(/[^\d]/g, "");

const DEFAULT_MESSAGE = encodeURIComponent(
  "Hi OneDial Africa team — I'd like to know more about your services."
);

export default function WhatsAppButton() {
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setPulse(false), 6000);
    return () => clearTimeout(t);
  }, []);

  const href = `https://wa.me/${WHATSAPP_NUMBER}?text=${DEFAULT_MESSAGE}`;

  return (
    <div
      className="fixed z-[80] bottom-24 right-4 sm:bottom-28 sm:right-6"
      data-testid="whatsapp-floating-container"
      style={{ marginBottom: "env(safe-area-inset-bottom)" }}
    >
      {open && (
        <div
          data-testid="whatsapp-popup"
          className="mb-3 w-72 sm:w-80 rounded-2xl bg-white border border-brand-line shadow-2xl overflow-hidden animate-fade-up"
        >
          <div className="bg-[#075E54] text-white px-4 py-3 flex items-start gap-3">
            <div className="h-9 w-9 rounded-full bg-white/15 flex items-center justify-center shrink-0">
              <MessageCircle className="h-5 w-5" />
            </div>
            <div className="flex-1 leading-tight">
              <div className="font-display font-bold text-sm">OneDial Africa</div>
              <div className="text-[11px] text-white/80">Customer Support · Typically replies fast</div>
            </div>
            <button
              type="button"
              data-testid="whatsapp-close"
              onClick={() => setOpen(false)}
              className="text-white/80 hover:text-white"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="p-4 bg-[#ECE5DD]/60">
            <div className="bg-white rounded-lg rounded-tl-none px-3 py-2.5 text-sm text-brand-ink shadow-sm">
              <p className="font-semibold">Hi there 👋</p>
              <p className="mt-1 text-brand-muted">
                Got a question about calling, eSIM, wallet or transfers? Chat with us on WhatsApp.
              </p>
            </div>
          </div>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-start-chat"
            onClick={() => setOpen(false)}
            className="block bg-[#25D366] hover:bg-[#1DA851] transition-colors text-white text-center font-semibold text-sm py-3"
          >
            Start chat on WhatsApp
          </a>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="whatsapp-floating-button"
        aria-label="Chat with us on WhatsApp"
        className="relative inline-flex items-center justify-center h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-[#25D366] hover:bg-[#1DA851] text-white shadow-xl transition-all hover:-translate-y-0.5"
      >
        {pulse && (
          <span className="pointer-events-none absolute inset-0 rounded-full bg-[#25D366] opacity-60 animate-ping" aria-hidden />
        )}
        {/* WhatsApp glyph (SVG) */}
        <svg viewBox="0 0 32 32" className="relative h-7 w-7 sm:h-8 sm:w-8" fill="currentColor" aria-hidden>
          <path d="M19.11 17.21c-.28-.14-1.66-.82-1.92-.91-.26-.1-.45-.14-.64.14-.19.28-.74.91-.91 1.1-.17.19-.34.21-.62.07-.28-.14-1.19-.44-2.27-1.4-.84-.75-1.4-1.67-1.57-1.95-.16-.28-.02-.43.12-.57.13-.13.28-.34.42-.51.14-.17.19-.28.28-.47.09-.19.05-.36-.02-.5-.07-.14-.64-1.55-.88-2.13-.23-.55-.47-.48-.64-.49l-.55-.01c-.19 0-.5.07-.76.36-.26.28-1 1-1 2.44 0 1.44 1.02 2.83 1.16 3.02.14.19 2.01 3.07 4.87 4.31.68.29 1.21.46 1.62.59.68.21 1.3.18 1.79.11.55-.08 1.66-.68 1.9-1.34.23-.66.23-1.22.16-1.34-.06-.12-.25-.19-.53-.33zM16.02 5.34c-5.92 0-10.72 4.8-10.72 10.72 0 1.89.49 3.72 1.43 5.34l-1.51 5.51 5.65-1.48a10.7 10.7 0 005.15 1.31h.01c5.92 0 10.72-4.8 10.72-10.72-.01-5.92-4.81-10.68-10.73-10.68zm0 19.6h-.01a8.86 8.86 0 01-4.51-1.24l-.32-.19-3.35.88.89-3.27-.21-.34a8.84 8.84 0 01-1.35-4.71c0-4.89 3.98-8.87 8.87-8.87 2.37 0 4.59.92 6.27 2.6a8.81 8.81 0 012.6 6.28c0 4.89-3.98 8.86-8.88 8.86z"/>
        </svg>
      </button>
    </div>
  );
}
